import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, Fragment } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LogOut, Truck, Settings as SettingsIcon, Plus, FileText, Car, TrendingUp, ClipboardList, Package, Users, Briefcase, ExternalLink, AlertCircle, Upload, UploadCloud, FileQuestion, FolderOpen, ChevronDown, ChevronUp, Boxes, UserPlus, X, Clock, Phone, ShoppingCart, PoundSterling } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import GlobalUploadModal from '../components/GlobalUploadModal';
import { toast } from 'sonner';
import { useBranch } from '../context/BranchContext';
import Quotes from './Quotes';
import Proforma from './Proforma';
import ToolsDropdown from '../components/ToolsDropdown';
import NotificationBars from '../components/NotificationBars';
import { StockRequestModal } from '../components/StockRequestModal';
import { useSalesFigures } from '../hooks/useSalesFigures';
import HubTileArt, { HubTileArtStyles, HubTileLogoBadge, getTileArtKey, getTileInnerStyle } from '../components/HubTileArt';
import HubDarkBackground from '../components/HubDarkBackground';
import { getPageZoom } from '../utils/modalViewport';
import { useV3Shell } from '../v3/V3ShellContext';
import SpaceLinkCards from '../spaces/SpaceLinkCards';
import SpaceLayoutView from '../spaces/SpaceLayoutView';

// UI V3: the Home cards use the V3 glass colours (CSS variables set by the V3 shell), so they follow UI V3 Dark / Light.
const V3_CARD_OUTER = {
  background: 'var(--v3-glass-2)',
  backdropFilter: 'blur(var(--v3-blur-2)) saturate(1.5)',
  WebkitBackdropFilter: 'blur(var(--v3-blur-2)) saturate(1.5)',
  border: '1px solid var(--v3-glass-edge)',
  boxShadow: 'inset 0 1px 0 var(--v3-glass-highlight), inset 0 -1px 0 var(--v3-glass-lowlight), var(--v3-shadow-glass)',
};

// `spaceView` ('sales' | 'admin') is set by pages/HomeGate.jsx for the Sales Space and Admin Space dashboards: the tile grid is left out, the key links show along the top as small
// cards, and the area below them is left empty for the views being built. Without it (the Default Space) this is the home page exactly as it was.
// `spaceView` 'layout' (with `spaceLayout`, the layout itself) is a Space whose home page was arranged in Settings > User Interface > Spaces Builder.
const Hub = ({ user, onLogout, spaceView = null, spaceLayout = null }) => {
  const navigate = useNavigate();
  // Home page background theme is a personal, per-user preference (Settings > General),
  // not an admin-controlled feature flag — defaults to "light" for every user.
  // Inside the V3 shell the card theme is the person's UI V3 Dark / Light choice; everywhere else it is their own Home page background preference.
  const v3Shell = useV3Shell();
  const v3On = !!v3Shell;
  const darkGlassCardsEnabled = v3On ? v3Shell.theme === 'dark' : user?.home_theme === 'dark';
  const cardTitleClass = v3On ? '' : (darkGlassCardsEnabled ? 'text-white' : 'text-gray-800');
  const salesFigures = useSalesFigures();
  const [forecastPopup, setForecastPopup] = useState(null); // { top, right } in page pixels while the mouse is over the Forecast pill
  const [currentPage, setCurrentPage] = useState(1);
  const [apps, setApps] = useState([]);
  const [quickLinks, setQuickLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchDashboardCards, setBranchDashboardCards] = useState({});
  const appsPerPage = 16;
  const { getEffectiveBranchId } = useBranch();
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [linksDropdownOpen, setLinksDropdownOpen] = useState(false);
  const [showGlobalUploadModal, setShowGlobalUploadModal] = useState(false);
  const [showTransportCSVModal, setShowTransportCSVModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showNewAccountsModal, setShowNewAccountsModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showStockRequestModal, setShowStockRequestModal] = useState(false);
  const [stockRequestDropdownOpen, setStockRequestDropdownOpen] = useState(false);
  const [stockMgmtLaunchOpen, setStockMgmtLaunchOpen] = useState(false);
  const [stockMgmtMenuPos, setStockMgmtMenuPos] = useState({ x: 0, y: 0 });
  const stockRequestDropdownRef = useRef(null);
  const [showWeeklyUpdateModal, setShowWeeklyUpdateModal] = useState(false);
  const [weeklyUpdateProgress, setWeeklyUpdateProgress] = useState({
    currentDay: '', currentVehicle: '', processedVehicles: 0, totalVehicles: 0,
    processedDays: 0, totalDays: 7, isComplete: false
  });

  const enconOrange = '#ee7203';
  const enconBlack = '#1d1d1b';
  const enconGrey = '#9d9d9c';
  const enconLightGrey = '#ededed';
  const lightViolet = '#8b5cf6';
  // The dark page background (graphite gradient with dots and waves) lives in
  // components/HubDarkBackground.jsx — only ever painted when the user's personal Home Page
  // Background preference is 'dark'.

  // Card look — light theme keeps its existing frosted-white glass unchanged; dark theme
  // (home_theme === 'dark') switches to the dark-orange neon glass spec from design_guidelines.json.
  const glassCardOuterStyle = v3On ? V3_CARD_OUTER : darkGlassCardsEnabled ? {
    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.09) 0%, rgba(238, 114, 3, 0.04) 50%, rgba(0, 0, 0, 0.40) 100%)',
    backdropFilter: 'blur(24px) saturate(1.6)', WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
    border: '1px solid rgba(255, 140, 26, 0.22)',
    boxShadow: 'inset 0 1.5px 0 rgba(255, 255, 255, 0.25), inset 0 -1px 0 rgba(0, 0, 0, 0.5), 0 12px 32px rgba(0, 0, 0, 0.65), 0 0 15px rgba(238, 114, 3, 0.12)',
  } : {
    background: 'linear-gradient(160deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.07) 100%)',
    backdropFilter: 'blur(20px) saturate(1.5)', WebkitBackdropFilter: 'blur(20px) saturate(1.5)',
    border: '1px solid rgba(255,255,255,0.18)',
    boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.2)',
  };
  const innerGradientStyle = darkGlassCardsEnabled ? {
    background: 'linear-gradient(135deg, rgba(30, 10, 3, 0.85) 0%, rgba(15, 4, 1, 0.95) 100%)',
    backdropFilter: 'blur(14px) saturate(1.3)', WebkitBackdropFilter: 'blur(14px) saturate(1.3)',
    border: '1.5px solid rgba(255, 106, 0, 0.60)',
    boxShadow: 'inset 0 1px 0 rgba(255, 180, 100, 0.3), inset 0 0 20px rgba(238, 114, 3, 0.15), 0 0 18px rgba(255, 106, 0, 0.35)',
  } : {
    background: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(14px) saturate(1.3)', WebkitBackdropFilter: 'blur(14px) saturate(1.3)',
    border: `1.5px solid ${enconOrange}`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), inset 0 0 24px rgba(255,255,255,0.02), 0 4px 14px rgba(0,0,0,0.25)',
  };
  const innerGradientDisabledStyle = {
    background: 'rgba(255,255,255,0.03)',
    backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
  };
  // Neon glow applied to app-tile icons only in dark mode (per design_guidelines.json neon_icon_badge_spec).
  const iconGlowStyle = darkGlassCardsEnabled
    ? { color: '#ff8c1a', filter: 'drop-shadow(0 0 8px rgba(255, 106, 0, 0.85)) drop-shadow(0 0 20px rgba(238, 114, 3, 0.5))' }
    : { color: enconOrange };
  const iconGlowDisabledStyle = darkGlassCardsEnabled ? { color: '#6b6b6b' } : { color: '#9ca3af' };
  const cardTitleStyle = v3On ? { color: 'var(--v3-ink)' } : (darkGlassCardsEnabled ? { textShadow: '0 2px 4px rgba(0,0,0,0.8)' } : undefined);
  const iconStrokeWidth = 1.5;
  const tileTheme = darkGlassCardsEnabled ? 'dark' : 'light';
  // Illustrated tile visual (dark and light variants). Returns null when a tile has no illustration,
  // in which case the existing icon rendering is used. Tiles that carry a logo file (Staffology, CRM)
  // show that real logo on a glass badge above the illustration, in its own colours.
  const renderTileArt = (artKey, customLogo, title) => {
    if (!artKey) return null;
    if (customLogo) {
      return (
        <div className="absolute inset-0 flex flex-col items-center px-1.5 pt-1.5">
          <HubTileLogoBadge src={customLogo} alt={title} artKey={artKey} />
          <div className="relative flex-1 min-h-0 w-full">
            <HubTileArt artKey={artKey} theme={tileTheme} className="absolute inset-0 w-full h-full" />
          </div>
        </div>
      );
    }
    return <HubTileArt artKey={artKey} theme={tileTheme} className="absolute inset-0 w-full h-full" />;
  };
  const stockMgmtViews = [
    { id: 'stock-ordering', label: 'Stock Ordering', icon: Package },
    { id: 'stock-efficiency', label: 'Stock Efficiency', icon: TrendingUp },
    { id: 'stocktake', label: 'Stocktake', icon: ClipboardList },
  ];

  const [proformaDropdownOpen, setProformaDropdownOpen] = useState(false);
  const [quotesDropdownOpen, setQuotesDropdownOpen] = useState(false);
  const [proformaModalMode, setProformaModalMode] = useState(null);
  const [quotesModalMode, setQuotesModalMode] = useState(null);
  const proformaDropdownRef = useRef(null);
  const quotesDropdownRef = useRef(null);
  const stockMgmtLaunchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (proformaDropdownRef.current && !proformaDropdownRef.current.contains(event.target)) setProformaDropdownOpen(false);
      if (quotesDropdownRef.current && !quotesDropdownRef.current.contains(event.target)) setQuotesDropdownOpen(false);
      if (stockRequestDropdownRef.current && !stockRequestDropdownRef.current.contains(event.target)) setStockRequestDropdownOpen(false);
      if (stockMgmtLaunchRef.current && !stockMgmtLaunchRef.current.contains(event.target)) setStockMgmtLaunchOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const iconMap = {
    'Truck': Truck, 'FileText': FileText, 'Car': Car, 'Settings': SettingsIcon, 'TrendingUp': TrendingUp,
    'ClipboardList': ClipboardList, 'Package': Package, 'Users': Users, 'Briefcase': Briefcase,
    'ExternalLink': ExternalLink, 'AlertCircle': AlertCircle, 'Boxes': Boxes, 'PoundSterling': PoundSterling,
  };

  const gradientBorder = 'linear-gradient(135deg, #ee7203 0%, #8b5cf6 100%)';

  const hasPermission = (permissionKey) => {
    if (!permissionKey) return true;
    if (user?.role === 'super_admin') return true;
    return user?.[permissionKey] || false;
  };

  const effectiveBranchId = getEffectiveBranchId();

  useEffect(() => {
    loadApps(); loadQuickLinks(); loadLinkGroups(); loadBranchDashboardCards();
  }, [user, effectiveBranchId]);

  const [linkGroups, setLinkGroups] = useState([]);

  const loadBranchDashboardCards = async () => {
    if (!effectiveBranchId) { setBranchDashboardCards({}); return; }
    try {
      const response = await api.get(`${API}/branches/${effectiveBranchId}/dashboard-cards`);
      setBranchDashboardCards(response.data?.cards || {});
    } catch (error) {
      console.error('Error loading branch dashboard card settings:', error);
      setBranchDashboardCards({});
    }
  };

  const loadApps = async () => {
    try {
      const url = effectiveBranchId ? `${API}/hub-apps` : `${API}/hub-apps`;
      const response = await api.get(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const transformedApps = response.data.map(app => {
        // The B29 Transfers card follows the Stock Transfer permission (on unless it has been switched off for the user).
        const hasAccess = app.route === '/b29-stock-transfer'
          ? (user?.role === 'super_admin' || user?.can_access_stock_transfer !== false)
          : hasPermission(app.permission_key);
        return {
          id: app.id, title: app.name, description: app.description,
          icon: app.icon_url ? null : iconMap[app.icon_type], customLogo: app.icon_url,
          route: app.route, available: hasAccess, color: hasAccess ? app.color : enconGrey,
          isExternalLink: app.is_external, externalUrl: app.is_external ? app.route : null,
        };
      });
      setApps(transformedApps);
    } catch (error) { console.error('Error loading hub apps:', error); }
    finally { setLoading(false); }
  };

  const loadQuickLinks = async () => {
    try {
      const url = effectiveBranchId ? `${API}/quick-links` : `${API}/quick-links`;
      const response = await api.get(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setQuickLinks(response.data);
    } catch (error) { console.error('Error loading quick links:', error); }
  };

  const loadLinkGroups = async () => {
    try {
      const url = effectiveBranchId ? `${API}/quick-link-groups` : `${API}/quick-link-groups`;
      const response = await api.get(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setLinkGroups(response.data);
    } catch (error) { console.error('Error loading link groups:', error); }
  };

  const getLinksForGroup = (groupId) => quickLinks.filter(link => (link.group_id || 'default') === groupId).sort((a, b) => a.order - b.order);

  const handleQuickLinkClick = (link) => {
    if (link.url.startsWith('http')) window.open(link.url, '_blank', 'noopener,noreferrer');
    else navigate(link.url);
  };

  const handleTransportCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post(`${API}/upload/contribution-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const orderCount = response.data.records_processed || response.data.order_count || 0;
      toast.success(`CSV uploaded: ${file.name} (${orderCount} orders). Updating transport data...`);
      setShowTransportCSVModal(false);
      await updateWeekCSVData();
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error('Failed to upload CSV file. Please try again.');
    } finally { setUploading(false); }
  };

  const updateWeekCSVData = async () => {
    try {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const weekStart = new Date(today.setDate(diff));
      const weekDates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + i);
        weekDates.push(date.toISOString().split('T')[0]);
      }
      setShowWeeklyUpdateModal(true);
      setWeeklyUpdateProgress({ currentDay: '', currentVehicle: '', processedVehicles: 0, totalVehicles: 0, processedDays: 0, totalDays: 7, isComplete: false });
      let processedDays = 0;
      for (const dateStr of weekDates) {
        const dayName = new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
        setWeeklyUpdateProgress(prev => ({ ...prev, currentDay: dayName, processedDays: processedDays }));
        try {
          const response = await api.get(`${API}/logs/${dateStr}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
          if (response.data && response.data.vehicles && response.data.vehicles.length > 0) {
            const vehicles = response.data.vehicles;
            setWeeklyUpdateProgress(prev => ({ ...prev, totalVehicles: vehicles.length, processedVehicles: 0 }));
            let updatedVehicles = [...vehicles];
            let processedVehicles = 0;
            for (const vehicle of updatedVehicles) {
              const vehicleIndex = updatedVehicles.findIndex(v => v.id === vehicle.id);
              let updatedVehicle = { ...vehicle };
              setWeeklyUpdateProgress(prev => ({ ...prev, currentVehicle: updatedVehicle.vehicle_registration, processedVehicles: processedVehicles }));
              if (vehicle.entries && vehicle.entries.length > 0) {
                for (let i = 0; i < updatedVehicle.entries.length; i++) {
                  const entry = updatedVehicle.entries[i];
                  if (entry.order_number) {
                    try {
                      const [contribResponse, salesResponse] = await Promise.all([
                        api.get(`${API}/upload/contribution-data/${entry.order_number}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => ({ data: { contribution: null } })),
                        api.get(`${API}/upload/sales-order-value/${entry.order_number}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => ({ data: { sales_order_value: null } }))
                      ]);
                      if (contribResponse.data.contribution !== null) updatedVehicle.entries[i].contribution = contribResponse.data.contribution;
                      if (salesResponse.data.sales_order_value !== null) updatedVehicle.entries[i].sales_order_value = salesResponse.data.sales_order_value;
                    } catch (error) { console.error(`Error fetching CSV for order ${entry.order_number}:`, error); }
                  }
                }
              }
              updatedVehicles[vehicleIndex] = updatedVehicle;
              processedVehicles++;
              setWeeklyUpdateProgress(prev => ({ ...prev, processedVehicles: processedVehicles }));
            }
            for (const vehicle of updatedVehicles) {
              try {
                const vehicleDataWithVersion = { ...vehicle, _save_date: dateStr };
                await api.put(`${API}/logs/${dateStr}/vehicle/${vehicle.id}`, vehicleDataWithVersion, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
              } catch (error) { console.error(`Error saving vehicle ${vehicle.id} for ${dateStr}:`, error); }
            }
          }
        } catch (error) {
          if (error.response?.status !== 404) console.error(`Error processing date ${dateStr}:`, error);
        }
        processedDays++;
        setWeeklyUpdateProgress(prev => ({ ...prev, processedDays: processedDays }));
      }
      setWeeklyUpdateProgress(prev => ({ ...prev, isComplete: true }));
      setTimeout(() => { setShowWeeklyUpdateModal(false); }, 2000);
      toast.success('✅ Week CSV data updated successfully!');
    } catch (error) {
      console.error('Error during weekly CSV update:', error);
      toast.error('Failed to update weekly CSV data');
      setShowWeeklyUpdateModal(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: enconLightGrey }}>
        <div className="text-center">
          <div className="text-xl font-semibold" style={{ color: enconOrange }}>Loading...</div>
        </div>
      </div>
    );
  }

  const mobileMenuButtons = (user?.role === 'super_admin' || user?.can_access_global_upload) ? [
    { icon: Upload, label: 'Upload Data', onClick: () => setShowGlobalUploadModal(true), color: enconOrange }
  ] : [];

  // Sales figures — shown only here, in the Home page's global sub-header (no longer an
  // independent bar on every page). White capsules with black text over the orange/white
  // sub-header gradient. Only the "Sales" (actual_sales) figure is colour-coded green/red
  // depending on whether today's Sales Tracker upload has landed (`is_current`) — every
  // other figure just uses plain black text.
  const showSalesFigures = !salesFigures.hideSalesBar && salesFigures.hasData;
  // Pop-up under the Forecast pill while the mouse is over it. Positions are in the page's own CSS pixels (getPageZoom
  // undoes the 0.8 page zoom on laptops / iPads) because the pop-up is drawn on the page via a portal, not inside the header.
  const openForecastPopup = (e) => {
    const zoom = getPageZoom();
    const r = e.currentTarget.getBoundingClientRect();
    setForecastPopup({ top: r.bottom / zoom + 8, right: window.innerWidth / zoom - r.right / zoom });
  };

  // Every figure pill's writing is `text-black` — black on the light sub-header, and the header's own dark styling
  // (HEADER_GLASS_CSS in AppHeader.jsx) turns `text-black…` text white on the dark one. Never a colour.
  // `onHoverStart` (optional) is called when the mouse enters the pill; leaving it closes the Forecast pop-up.
  const figurePill = (label, value, extraClass = '', onHoverStart = null) => (
    <div
      className={`flex items-center gap-1 rounded-full px-2.5 py-1 flex-shrink-0 bg-white ${extraClass}`}
      onMouseEnter={onHoverStart || undefined}
      onMouseLeave={onHoverStart ? () => setForecastPopup(null) : undefined}
      data-testid={`sales-figure-pill-${label.toLowerCase().replace(/[^a-z]/g, '')}`}
    >
      <span className="text-black text-[9px] uppercase tracking-wide">{label}</span>
      <span className="font-bold text-[11px] sm:text-xs whitespace-nowrap text-black">{value}</span>
    </div>
  );
  const subHeaderLeft = showSalesFigures ? (
    <>
      <div className="flex items-center gap-1 rounded-full px-2.5 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
        <span className="text-white font-bold text-[11px] sm:text-xs">{salesFigures.dayName}</span>
      </div>
      {figurePill('Stock', salesFigures.formatCurrency(salesFigures.salesData?.inventory_cost))}
      {figurePill('INV', salesFigures.formatCurrency(salesFigures.salesData?.daily_invoiced))}
      {figurePill("Y'day", salesFigures.formatCurrency(salesFigures.salesData?.daily_intake))}
    </>
  ) : null;
  const subHeaderCenter = null;
  const subHeaderRight = showSalesFigures ? (
    <>
      {figurePill('Sales', salesFigures.formatCurrency(salesFigures.salesData?.actual_sales))}
      {figurePill('Forecast', salesFigures.formatCurrency(salesFigures.salesData?.forecast_eom), '', openForecastPopup)}
    </>
  ) : null;

  const renderStockManagementCard = (key) => (
    <div key={key} className="relative">
      <button onClick={(e) => { setStockMgmtMenuPos({ x: e.clientX, y: e.clientY }); setStockMgmtLaunchOpen(o => !o); }}
        className={`relative aspect-square w-full rounded-3xl p-2.5 sm:p-3 flex flex-col items-center transition-transform duration-300 ease-out hover:scale-[1.02] group ${darkGlassCardsEnabled ? 'hover:shadow-[0_16px_40px_rgba(0,0,0,0.8),0_0_25px_rgba(255,106,0,0.40)]' : ''}`}
        style={glassCardOuterStyle} data-testid="hub-card-stock-management">
        <span className="absolute top-2 left-2 w-5 h-5 rounded-full bg-white/50 blur-md pointer-events-none" />
        <span className="absolute bottom-2.5 right-2.5 w-4 h-4 rounded-full bg-white/35 blur-md pointer-events-none" />
        <div className="relative flex-1 w-full rounded-2xl flex items-center justify-center overflow-hidden mb-1.5 sm:mb-2" style={getTileInnerStyle('stock-management', tileTheme)}>
          {renderTileArt('stock-management', null, 'Stock Management')}
        </div>
        <p className={`text-[11px] sm:text-sm font-bold ${cardTitleClass} text-center leading-tight px-1`} style={cardTitleStyle}>Stock Management</p>
      </button>
    </div>
  );

  const stockMgmtMenu = stockMgmtLaunchOpen && (
    <div ref={stockMgmtLaunchRef} className="fixed w-48 bg-white rounded-2xl border-2 shadow-xl overflow-hidden z-[9999]"
      style={{ borderColor: enconOrange, left: stockMgmtMenuPos.x, top: stockMgmtMenuPos.y }} data-testid="stock-mgmt-launch-menu">
      {stockMgmtViews.map((v, i) => {
        const VIcon = v.icon;
        return (
          <button key={v.id}
            onClick={() => { setStockMgmtLaunchOpen(false); navigate(`/stock-management?view=${v.id}`); }}
            className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 transition-colors text-left ${i < stockMgmtViews.length - 1 ? 'border-b' : ''}`}
            style={{ borderColor: '#fee2e2' }}
            data-testid={`stock-mgmt-launch-option-${v.id}`}>
            <VIcon className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} />
            <span className="font-medium text-sm text-gray-700">{v.label}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    // display: 'flow-root' establishes a new block-formatting-context so
    // AppHeader's internal marginTop spacer (used to offset its own fixed
    // height) doesn't margin-collapse through this div and push its own
    // background paint origin down, away from the true page top.
    // The dark gradient itself is NOT painted on this div (it grows with
    // content height, which made the gradient appear to pan/scroll with the
    // cards). Instead it's a separate `position: fixed` layer below, pinned
    // to the viewport so it never moves regardless of scroll position.
    <div className="min-h-screen" style={{ background: (darkGlassCardsEnabled || v3On) ? undefined : '#ffffff', display: 'flow-root' }}>
      <HubTileArtStyles />
      {darkGlassCardsEnabled && !v3On && <HubDarkBackground />}
      <AppHeader user={user} onLogout={onLogout} rightButtons={null} mobileMenuButtons={mobileMenuButtons}
        subHeaderLeft={subHeaderLeft} subHeaderCenter={subHeaderCenter} subHeaderRight={subHeaderRight} />

      <div className="px-4 lg:px-6 pb-12 pt-[20px] min-h-screen">
        <div className="max-w-[1800px] mx-auto">
          {!spaceView && apps.filter(app => app.available).length > appsPerPage && (
            <div className="hidden md:flex justify-center items-center gap-4 mb-6">
              <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg border-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: enconOrange, color: enconOrange }}>Previous</button>
              <span className="text-sm font-semibold">Page {currentPage} of {Math.ceil(apps.filter(app => app.available).length / appsPerPage)}</span>
              <button onClick={() => setCurrentPage(prev => Math.min(Math.ceil(apps.filter(app => app.available).length / appsPerPage), prev + 1))}
                disabled={currentPage === Math.ceil(apps.filter(app => app.available).length / appsPerPage)}
                className="px-4 py-2 rounded-lg border-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: enconOrange, color: enconOrange }}>Next</button>
            </div>
          )}

          <div className="md:hidden flex justify-center gap-3 mb-4 px-2">
            <div className="relative flex-1 max-w-[calc(50%-6px)]">
              <button onClick={() => { setAdminDropdownOpen(!adminDropdownOpen); setLinksDropdownOpen(false); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white border-2 transition-all"
                style={{ borderColor: enconOrange }}>
                <span className="font-semibold text-sm" style={{ color: enconOrange }}>Admin</span>
                {adminDropdownOpen ? <ChevronUp className="w-4 h-4" style={{ color: enconOrange }} /> : <ChevronDown className="w-4 h-4" style={{ color: enconOrange }} />}
              </button>
              {adminDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border-2 shadow-lg z-50 overflow-hidden" style={{ borderColor: enconOrange }}>
                  <button onClick={() => { setProformaModalMode('create'); setAdminDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors border-b" style={{ borderColor: '#fee2e2' }}>
                    <FileText className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium text-sm" style={{ color: enconOrange }}>Pro Forma</span>
                  </button>
                  <button onClick={() => { setQuotesModalMode('create'); setAdminDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors border-b" style={{ borderColor: '#fee2e2' }}>
                    <FileQuestion className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium text-sm" style={{ color: enconOrange }}>Quotes</span>
                  </button>
                  <button onClick={() => { navigate('/stock-transfer'); setAdminDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors border-b" style={{ borderColor: '#fee2e2' }}>
                    <Package className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium text-sm" style={{ color: enconOrange }}>Stock Transfers</span>
                  </button>
                  <button onClick={() => { setShowStockRequestModal(true); setAdminDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors border-b" style={{ borderColor: '#fee2e2' }}>
                    <ShoppingCart className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium text-sm" style={{ color: enconOrange }}>Stock Request</span>
                  </button>
                  <button onClick={() => { navigate('/po-expenses'); setAdminDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors">
                    <ClipboardList className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium text-sm" style={{ color: enconOrange }}>P.O. Expenses</span>
                  </button>
                  <button onClick={() => { navigate('/price-increases'); setAdminDropdownOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors">
                    <TrendingUp className="w-4 h-4" style={{ color: enconOrange }} /><span className="font-medium text-sm" style={{ color: enconOrange }}>Price Increases</span>
                  </button>
                </div>
              )}
            </div>
            {quickLinks.length > 0 && (
              <div className="relative flex-1 max-w-[calc(50%-6px)]">
                <button onClick={() => { setLinksDropdownOpen(!linksDropdownOpen); setAdminDropdownOpen(false); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white border-2 transition-all" style={{ borderColor: enconOrange }}>
                  <span className="font-semibold text-sm" style={{ color: enconOrange }}>Links</span>
                  {linksDropdownOpen ? <ChevronUp className="w-4 h-4" style={{ color: enconOrange }} /> : <ChevronDown className="w-4 h-4" style={{ color: enconOrange }} />}
                </button>
                {linksDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border-2 shadow-lg z-50 overflow-hidden" style={{ borderColor: enconOrange }}>
                    {linkGroups.map((group) => {
                      const groupLinks = getLinksForGroup(group.id);
                      if (groupLinks.length === 0) return null;
                      return groupLinks.map((link, linkIndex) => (
                        <button key={link.id} onClick={() => { handleQuickLinkClick(link); setLinksDropdownOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                          style={{ borderBottom: linkIndex < groupLinks.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                          <span className="font-medium text-sm" style={{ color: group.color }}>{link.name}</span>
                        </button>
                      ));
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* A Sales Space / Admin Space home page shows the key link tiles where the notification boxes were; the Default Space keeps the notification boxes. */}
          {spaceView === 'layout' ? <SpaceLayoutView user={user} layout={spaceLayout} /> : spaceView ? <SpaceLinkCards user={user} view={spaceView} /> : <div className="hidden md:block"><NotificationBars user={user} /></div>}

          <StockRequestModal isOpen={showStockRequestModal} onClose={() => setShowStockRequestModal(false)} />

          {quotesModalMode !== null && (<Quotes.Modal isOpen={true} onClose={() => setQuotesModalMode(null)} mode={quotesModalMode} user={user} />)}
          {proformaModalMode !== null && (<Proforma.Modal isOpen={true} onClose={() => setProformaModalMode(null)} mode={proformaModalMode} user={user} />)}

          {showNewAccountsModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => setShowNewAccountsModal(false)}>
              <div className="bg-white rounded-xl shadow-2xl flex flex-col" style={{ width: '95vw', height: '92vh' }} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b" style={{ backgroundColor: enconOrange }}>
                  <div className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-white" /><span className="font-bold text-white text-lg">New Accounts</span></div>
                  <button onClick={() => setShowNewAccountsModal(false)} className="text-white hover:bg-white/20 rounded-full p-1 transition-colors" data-testid="close-new-accounts-modal"><X className="w-5 h-5" /></button>
                </div>
                <iframe src="https://forms.office.com/pages/responsepage.aspx?id=PlJxjHhvUUWoFOzwm7bCYumXt2cxmMBAtzfRqtGCOwxUREFWSExLOFBWT1o3QTgyQ0xDVjRaR01VQi4u&route=shorturl" className="flex-1 w-full border-0 rounded-b-xl" title="New Accounts Form" allow="clipboard-write" />
              </div>
            </div>
          )}

          <div className="flex justify-center">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-6 w-full max-w-[1400px]">
            {apps
              .filter(app => app.available)
              .filter(() => !spaceView)
              .slice(0, window.innerWidth < 768 ? 100 : (currentPage - 1) * appsPerPage + appsPerPage)
              .slice(window.innerWidth < 768 ? 0 : (currentPage - 1) * appsPerPage)
              .map((app) => {
              const Icon = app.icon;
              const artKey = getTileArtKey(app.title);
              const isStaffology = app.title?.toLowerCase().includes('staffology');
              const isPICounts = app.title === 'PI Counts' || app.route === '/pi-counts';
              const canAccessPICounts = user?.role === 'super_admin' || user?.can_access_pi_counts;
              const canAccessStockManagement = (user?.role === 'super_admin' || user?.can_access_stock_management) && branchDashboardCards.stock_management !== false;

              if (isPICounts) {
                if (!canAccessPICounts && !canAccessStockManagement) return null;

                if (canAccessStockManagement && !canAccessPICounts) {
                  return renderStockManagementCard(app.id);
                }

                if (canAccessPICounts && !canAccessStockManagement) {
                  return (
                    <button key={app.id} onClick={() => navigate('/pi-counts')}
                      className={`relative aspect-square w-full rounded-3xl p-2.5 sm:p-3 flex flex-col items-center transition-transform duration-300 ease-out hover:scale-[1.02] group ${darkGlassCardsEnabled ? 'hover:shadow-[0_16px_40px_rgba(0,0,0,0.8),0_0_25px_rgba(255,106,0,0.40)]' : ''}`}
                      style={glassCardOuterStyle} data-testid="hub-card-pi-counts">
                      <span className="absolute top-2 left-2 w-5 h-5 rounded-full bg-white/50 blur-md pointer-events-none" />
                      <span className="absolute bottom-2.5 right-2.5 w-4 h-4 rounded-full bg-white/35 blur-md pointer-events-none" />
                      <div className="relative flex-1 w-full rounded-2xl flex items-center justify-center overflow-hidden mb-1.5 sm:mb-2" style={getTileInnerStyle('pi-counts', tileTheme)}>
                        {renderTileArt('pi-counts', null, 'PI Counts')}
                      </div>
                      <p className={`text-[11px] sm:text-sm font-bold ${cardTitleClass} text-center leading-tight px-1`} style={cardTitleStyle}>PI Counts</p>
                    </button>
                  );
                }

                return (
                  <Fragment key={app.id}>
                    {renderStockManagementCard(`${app.id}-mgmt`)}
                    <button onClick={() => navigate('/pi-counts')}
                      className={`relative aspect-square w-full rounded-3xl p-2.5 sm:p-3 flex flex-col items-center transition-transform duration-300 ease-out hover:scale-[1.02] group ${darkGlassCardsEnabled ? 'hover:shadow-[0_16px_40px_rgba(0,0,0,0.8),0_0_25px_rgba(255,106,0,0.40)]' : ''}`}
                      style={glassCardOuterStyle} data-testid="hub-card-pi-counts">
                      <span className="absolute top-2 left-2 w-5 h-5 rounded-full bg-white/50 blur-md pointer-events-none" />
                      <span className="absolute bottom-2.5 right-2.5 w-4 h-4 rounded-full bg-white/35 blur-md pointer-events-none" />
                      <div className="relative flex-1 w-full rounded-2xl flex items-center justify-center overflow-hidden mb-1.5 sm:mb-2" style={getTileInnerStyle('pi-counts', tileTheme)}>
                        {renderTileArt('pi-counts', null, 'PI Counts')}
                      </div>
                      <p className={`text-[11px] sm:text-sm font-bold ${cardTitleClass} text-center leading-tight px-1`} style={cardTitleStyle}>PI Counts</p>
                    </button>
                  </Fragment>
                );
              }

              return (
                <button key={app.id} disabled={!app.available}
                  onClick={() => {
                    if (!app.available) return;
                    if (isStaffology) { window.open('https://app.staffologyhr.co.uk/portal/logoff.aspx', '_blank', 'noopener,noreferrer'); return; }
                    if (app.isExternalLink) { window.open(app.externalUrl, '_blank', 'noopener,noreferrer'); }
                    else {
                      if (app.id === 'transport') sessionStorage.setItem('transport_selected_date', new Date().toISOString());
                      navigate(app.route);
                    }
                  }}
                  className={`relative aspect-square w-full rounded-3xl p-2.5 sm:p-3 flex flex-col items-center transition-transform duration-300 ease-out group ${app.available ? 'hover:scale-[1.02] cursor-pointer' : 'cursor-not-allowed'} ${darkGlassCardsEnabled ? 'hover:shadow-[0_16px_40px_rgba(0,0,0,0.8),0_0_25px_rgba(255,106,0,0.40)]' : ''}`}
                  style={glassCardOuterStyle} data-testid={`hub-card-${app.id}`}>
                  <span className="absolute top-2 left-2 w-5 h-5 rounded-full bg-white/50 blur-md pointer-events-none" />
                  <span className="absolute bottom-2.5 right-2.5 w-4 h-4 rounded-full bg-white/35 blur-md pointer-events-none" />
                  {!app.available && (<span className="absolute top-1.5 right-1.5 z-10 bg-white/90 text-gray-600 text-[8px] sm:text-[9px] font-bold uppercase tracking-wide px-1.5 sm:px-2 py-0.5 rounded-full">Coming Soon</span>)}
                  <div className="relative flex-1 w-full rounded-2xl flex items-center justify-center overflow-hidden mb-1.5 sm:mb-2" style={app.available ? (artKey ? getTileInnerStyle(artKey, tileTheme) : innerGradientStyle) : innerGradientDisabledStyle}>
                    {app.available && artKey ? renderTileArt(artKey, app.customLogo, app.title) : app.customLogo ? (
                      <div className="relative w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-lg bg-white/90 flex items-center justify-center p-2 transition-transform duration-300 ease-out group-hover:scale-110">
                        <img src={app.customLogo} alt={app.title} className="w-full h-full object-contain" />
                      </div>
                    ) : (
                      <Icon strokeWidth={iconStrokeWidth} className="relative w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 drop-shadow-sm transition-transform duration-300 ease-out group-hover:scale-110" style={app.available ? iconGlowStyle : iconGlowDisabledStyle} />
                    )}
                  </div>
                  <p className={`text-[11px] sm:text-sm font-bold ${cardTitleClass} text-center leading-tight line-clamp-2 px-1`} style={cardTitleStyle}>{app.title}</p>
                </button>
              );
            })}
            </div>
          </div>
        </div>
      </div>

      <GlobalUploadModal isOpen={showGlobalUploadModal} onClose={() => setShowGlobalUploadModal(false)}
        user={user} onSelectTransportCSV={() => setShowTransportCSVModal(true)} />

      <Dialog open={showTransportCSVModal} onOpenChange={setShowTransportCSVModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle className="text-2xl flex items-center gap-2" style={{ color: enconOrange }}><UploadCloud className="w-7 h-7" />Upload Transport CSV</DialogTitle></DialogHeader>
          <div className="py-6">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" style={{ borderColor: enconOrange }}>
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <UploadCloud className="w-10 h-10 mb-3" style={{ color: enconOrange }} />
                <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                <p className="text-xs text-gray-500">CSV file with Order No., Contribution Value, Sales Order Value</p>
              </div>
              <input type="file" accept=".csv" onChange={handleTransportCSVUpload} disabled={uploading} className="hidden" />
            </label>
            {uploading && (<div className="mt-4 text-center text-sm" style={{ color: enconOrange }}>Uploading...</div>)}
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t"><Button variant="outline" onClick={() => setShowTransportCSVModal(false)} className="px-6">Close</Button></div>
        </DialogContent>
      </Dialog>

      {showWeeklyUpdateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4" style={{ borderTop: `4px solid ${enconOrange}` }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: enconOrange }}>{weeklyUpdateProgress.isComplete ? '✅ Update Complete!' : '🔄 Updating Transport Data...'}</h2>
            {!weeklyUpdateProgress.isComplete && (
              <>
                <div className="mb-4">
                  <p className="text-gray-700 mb-2"><span className="font-semibold">Day:</span> {weeklyUpdateProgress.currentDay}</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div className="h-2 rounded-full transition-all duration-300" style={{ width: `${(weeklyUpdateProgress.processedDays / weeklyUpdateProgress.totalDays) * 100}%`, backgroundColor: enconOrange }} />
                  </div>
                  <p className="text-sm text-gray-600">Day {weeklyUpdateProgress.processedDays} of {weeklyUpdateProgress.totalDays}</p>
                </div>
                {weeklyUpdateProgress.currentVehicle && (
                  <div className="mb-4">
                    <p className="text-gray-700 mb-2"><span className="font-semibold">Vehicle:</span> {weeklyUpdateProgress.currentVehicle}</p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div className="h-2 rounded-full transition-all duration-300" style={{ width: `${(weeklyUpdateProgress.processedVehicles / (weeklyUpdateProgress.totalVehicles || 1)) * 100}%`, backgroundColor: enconOrange }} />
                    </div>
                    <p className="text-sm text-gray-600">Vehicle {weeklyUpdateProgress.processedVehicles} of {weeklyUpdateProgress.totalVehicles}</p>
                  </div>
                )}
                <p className="text-center text-gray-500 text-sm mt-4">Updating contribution values and sales data from CSV...</p>
              </>
            )}
            {weeklyUpdateProgress.isComplete && (
              <div className="text-center">
                <div className="text-6xl mb-4">✅</div>
                <p className="text-gray-700 mb-2">All contribution values have been updated for the week!</p>
                <p className="text-sm text-gray-500">This window will close automatically...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {stockMgmtMenu}

      {/* Forecast pill hover pop-up: this month's budget from the uploaded budget pack (info-bar-data returns it as `budget`). */}
      {forecastPopup && createPortal(
        <div
          className="fixed rounded-xl px-3 py-2 shadow-xl whitespace-nowrap text-left pointer-events-none"
          style={{ top: forecastPopup.top, right: forecastPopup.right, zIndex: 300, backgroundColor: enconBlack, border: `1.5px solid ${enconOrange}` }}
          data-testid="forecast-budget-popup"
        >
          <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: '#fbbf80' }}>
            {new Date().toLocaleString('en-GB', { month: 'long' })} budget
          </div>
          <div className="text-sm font-bold" style={{ color: '#ffffff' }}>
            {Number(salesFigures.salesData?.budget) > 0 ? salesFigures.formatCurrency(Number(salesFigures.salesData.budget)) : 'Not set'}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default Hub;
